import type { EngineInput, EngineResult, ProfitLeak } from '../estimate.types';
import { resolveEstimateRate, type EstimateRateType } from '../estimate.rates';
import { createEstimateItem, formatNumber } from '../estimateItemFactory';
import { suggestMaterials } from '../materialSuggestions';
import { multiplyMoney, sumMoney } from '../money';

function rate(type: EstimateRateType, input: EngineInput): number {
  return resolveEstimateRate(type, input.region, input.userRates);
}

function addItem(
  items: EngineResult['items'],
  input: EngineInput,
  data: {
    id: string;
    label: string;
    category: string;
    quantity: number;
    unit: 'm²' | 'mb' | 'm³' | 'ryczałt';
    rateType: EstimateRateType;
    pricingMode: 'area' | 'linear' | 'volume' | 'flat';
    calculation?: string;
    order: number;
  }
): void {
  if (data.quantity <= 0) return;
  items.push(
    createEstimateItem({
      id: data.id,
      label: data.label,
      category: data.category,
      quantity: data.quantity,
      unit: data.unit,
      unitPrice: rate(data.rateType, input),
      pricingMode: data.pricingMode,
      calculation: data.calculation,
      order: data.order,
    })
  );
}

function emptyPavingMeta(input: EngineInput, totalArea: number): EngineResult['meta'] {
  return {
    perimeter: 0,
    length: input.length ?? 0,
    width: input.width ?? 0,
    height: 0,
    floorArea: totalArea,
    openingsArea: 0,
    wallArea: 0,
    wallsGross: 0,
    doorsDeduction: 0,
    bigWindowsDeduction: 0,
    ceilingArea: 0,
    effectiveArea: totalArea,
    wallsNet: totalArea,
    totalWorkingArea: totalArea,
    calculations: {
      floorArea: `${formatNumber(totalArea)} m² brukarstwa`,
      perimeter: 'brukarstwo: obmiar branżowy',
      wallsGross: 'nie dotyczy',
      doorsDeduction: 'nie dotyczy',
      bigWindowsDeduction: 'nie dotyczy',
      wallsNet: 'nie dotyczy',
      totalWorkingArea: `${formatNumber(totalArea)} m²`,
      measurementMode: 'paving_basic',
      calculationNote: 'Brukarstwo MVP: silnik liczy proces technologiczny, a użytkownik może zmienić każdą decyzję.',
    },
  };
}

function createPavingLeaks(input: EngineInput, baseM3: number): ProfitLeak[] {
  const leaks: ProfitLeak[] = [];
  const area = input.pavingAreaM2 ?? 0;
  const edgeLength = input.edgeLengthM ?? 0;
  const baseThickness = input.baseThicknessCm ?? 25;

  if (area > 0 && edgeLength <= 0) {
    const unitRate = rate('paving_edges', input);
    const assumedLength = Math.round(Math.sqrt(area) * 4);
    const value = multiplyMoney(assumedLength, unitRate);
    const calculation = `${assumedLength} mb × ${unitRate} zł/mb = ${value} zł`;

    leaks.push({
      id: 'paving_missing_edges',
      type: 'custom',
      severity: 'warning',
      label: 'Brak obrzeży / krawężników',
      message: 'Bez obrzeży kostka może się rozjechać. To wróci jako poprawka z Twojej kieszeni.',
      clientPdfMessage: 'Obrzeża stabilizują nawierzchnię i poprawiają trwałość wykonania.',
      value,
      quantity: assumedLength,
      unit: 'mb',
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: createEstimateItem({
        id: 'paving_edges',
        label: 'Obrzeża / krawężniki',
        category: 'edges',
        quantity: assumedLength,
        unit: 'mb',
        unitPrice: unitRate,
        pricingMode: 'linear',
      }),
    });
  }

  if (area > 0 && baseThickness < 20) {
    const unitRate = rate('paving_base', input);
    const missingM3 = Math.max(0, area * ((20 - baseThickness) / 100));
    const value = multiplyMoney(missingM3, unitRate);
    const calculation = `${formatNumber(area)} m² × ${formatNumber((20 - baseThickness) / 100)} m = ${formatNumber(missingM3)} m³ × ${unitRate} zł/m³ = ${value} zł`;

    leaks.push({
      id: 'paving_thin_base',
      type: 'custom',
      severity: 'critical',
      label: 'Za cienka podbudowa',
      message: 'Podbudowa wygląda za cienko. Przy podjeździe ryzykujesz koleiny i reklamację.',
      value,
      quantity: missingM3,
      unit: 'm³',
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: createEstimateItem({
        id: 'paving_base_extra',
        label: 'Uzupełnienie podbudowy z kruszywa',
        category: 'base',
        quantity: missingM3,
        unit: 'm³',
        unitPrice: unitRate,
        pricingMode: 'volume',
      }),
    });
  }

  if (area > 0 && input.requiresGeotextile === false) {
    const unitRate = rate('paving_geotextile', input);
    const value = multiplyMoney(area, unitRate);
    const calculation = `${formatNumber(area)} m² × ${unitRate} zł/m² = ${value} zł`;

    leaks.push({
      id: 'paving_missing_geotextile',
      type: 'custom',
      severity: 'warning',
      label: 'Brak geowłókniny',
      message: 'Bez geowłókniny podbudowa może mieszać się z gruntem. To potrafi zjeść marżę na poprawkach.',
      value,
      quantity: area,
      unit: 'm²',
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: createEstimateItem({
        id: 'paving_geotextile',
        label: 'Geowłóknina',
        category: 'geotextile',
        quantity: area,
        unit: 'm²',
        unitPrice: unitRate,
        pricingMode: 'area',
      }),
    });
  }

  if (baseM3 > 0 && input.requiresSpoilRemoval === false) {
    const unitRate = rate('paving_spoil_removal', input);
    const value = multiplyMoney(baseM3, unitRate);
    const calculation = `${formatNumber(baseM3)} m³ × ${unitRate} zł/m³ = ${value} zł`;

    leaks.push({
      id: 'paving_missing_spoil_removal',
      type: 'custom',
      severity: 'warning',
      label: 'Brak wywozu urobku',
      message: 'Urobek sam nie zniknie. Jeśli nie doliczysz wywozu, robisz transport za darmo.',
      value,
      quantity: baseM3,
      unit: 'm³',
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: createEstimateItem({
        id: 'paving_spoil_removal',
        label: 'Wywóz urobku',
        category: 'spoil',
        quantity: baseM3,
        unit: 'm³',
        unitPrice: unitRate,
        pricingMode: 'volume',
      }),
    });
  }

  return leaks;
}

export function calculatePavingEstimate(input: EngineInput): EngineResult {
  const area = input.pavingAreaM2 ?? input.floorArea ?? 0;
  const edgeLength = input.edgeLengthM ?? 0;
  const baseThicknessM = (input.baseThicknessCm ?? 25) / 100;
  const beddingThicknessM = (input.beddingThicknessCm ?? 4) / 100;
  const baseM3 = area * baseThicknessM;
  const beddingM3 = area * beddingThicknessM;
  const spoilM3 = area * baseThicknessM;
  const items: EngineResult['items'] = [];

  addItem(items, input, {
    id: 'paving_excavation',
    label: 'Korytowanie gruntu',
    category: 'excavation',
    quantity: area,
    unit: 'm²',
    rateType: 'paving_excavation',
    pricingMode: 'area',
    order: 10,
  });

  if (input.requiresSpoilRemoval ?? true) {
    addItem(items, input, {
      id: 'paving_spoil_removal',
      label: 'Wywóz urobku',
      category: 'spoil',
      quantity: spoilM3,
      unit: 'm³',
      rateType: 'paving_spoil_removal',
      pricingMode: 'volume',
      calculation: `${formatNumber(area)} m² × ${formatNumber(baseThicknessM)} m = ${formatNumber(spoilM3)} m³`,
      order: 20,
    });
  }

  if (input.requiresGeotextile ?? true) {
    addItem(items, input, {
      id: 'paving_geotextile',
      label: 'Geowłóknina',
      category: 'geotextile',
      quantity: area,
      unit: 'm²',
      rateType: 'paving_geotextile',
      pricingMode: 'area',
      order: 30,
    });
  }

  addItem(items, input, {
    id: 'paving_base',
    label: 'Podbudowa z kruszywa',
    category: 'base',
    quantity: baseM3,
    unit: 'm³',
    rateType: 'paving_base',
    pricingMode: 'volume',
    calculation: `${formatNumber(area)} m² × ${formatNumber(baseThicknessM)} m = ${formatNumber(baseM3)} m³`,
    order: 40,
  });

  addItem(items, input, {
    id: 'paving_compaction',
    label: 'Zagęszczanie warstwowe',
    category: 'compaction',
    quantity: area,
    unit: 'm²',
    rateType: 'paving_compaction',
    pricingMode: 'area',
    order: 50,
  });

  addItem(items, input, {
    id: 'paving_bedding',
    label: 'Podsypka cementowo-piaskowa',
    category: 'bedding',
    quantity: beddingM3,
    unit: 'm³',
    rateType: 'paving_bedding',
    pricingMode: 'volume',
    calculation: `${formatNumber(area)} m² × ${formatNumber(beddingThicknessM)} m = ${formatNumber(beddingM3)} m³`,
    order: 60,
  });

  addItem(items, input, {
    id: 'paving_laying',
    label: 'Układanie kostki brukowej',
    category: 'laying',
    quantity: area,
    unit: 'm²',
    rateType: 'paving_laying',
    pricingMode: 'area',
    order: 70,
  });

  if (input.requiresCutting ?? true) {
    addItem(items, input, {
      id: 'paving_cutting',
      label: 'Docinki kostki',
      category: 'cutting',
      quantity: edgeLength > 0 ? edgeLength : Math.round(Math.sqrt(area) * 2),
      unit: 'mb',
      rateType: 'paving_cutting',
      pricingMode: 'linear',
      order: 80,
    });
  }

  addItem(items, input, {
    id: 'paving_edges',
    label: 'Obrzeża / krawężniki',
    category: 'edges',
    quantity: edgeLength,
    unit: 'mb',
    rateType: 'paving_edges',
    pricingMode: 'linear',
    order: 90,
  });

  addItem(items, input, {
    id: 'paving_joint_sand',
    label: 'Zasypanie fug / piasek',
    category: 'joint_sand',
    quantity: area,
    unit: 'm²',
    rateType: 'paving_joint_sand',
    pricingMode: 'area',
    order: 100,
  });

  addItem(items, input, {
    id: 'paving_final_compaction',
    label: 'Wibrowanie / końcowe zagęszczenie',
    category: 'final_compaction',
    quantity: area,
    unit: 'm²',
    rateType: 'paving_final_compaction',
    pricingMode: 'area',
    order: 110,
  });

  const cost = sumMoney(items.map((item) => item.totalNetto));
  const crewSize = input.crewSize ?? (area >= 80 ? 3 : 2);
  const estimatedDays = Math.max(1, Math.ceil(area / (crewSize * 25)));
  const result: EngineResult = {
    items,
    leaks: createPavingLeaks(input, baseM3),
    cost,
    scheduleEstimate: {
      crewSize,
      estimatedDays,
      stages: [
        'Korytowanie i wywóz urobku',
        'Geowłóknina i podbudowa',
        'Zagęszczanie warstwowe',
        'Podsypka i układanie kostki',
        'Docinki, fugowanie i końcowe wibrowanie',
      ],
      assumptions: [
        'MVP zakłada typowy dostęp do frontu robót.',
        'AI może zasugerować parametry, ale koszt liczy engine.',
      ],
    },
    meta: emptyPavingMeta(input, area),
  };

  return {
    ...result,
    meta: {
      ...result.meta,
      materialSuggestions: suggestMaterials({ ...input, tradeEngine: 'paving', pavingAreaM2: area }, result),
    },
  };
}
